/**
 * Relink tuning (M3 plan R19, spec §6.3, D-TBD 12/13): `src/data/reach-relink.json`, Zod-validated here. Repair
 * lists per region, delivery range and speed, the reboot defence (duration, objective hull, siege weight, waves,
 * guardians, allies) and lane-ring repair. Every value is a starting value; the "Relink" dev-panel group edits the
 * live object in place.
 */
import { z } from 'zod'
import relinkJson from '../data/reach-relink.json'
import type { RegionId } from '../galaxy/types'

const positive = z.number().positive()
const nonNeg = z.number().nonnegative()
const count = z.number().int().nonnegative()
const fraction = z.number().min(0).max(1)

/** A [min, max] range with min ≤ max. */
const range = z.tuple([positive, positive]).refine(([lo, hi]) => lo <= hi, { message: 'range min must not exceed max' })

const perRegion = <T extends z.ZodType>(item: T) => z.strictObject({ gate: item, mid: item, deep: item, rim: item })

const partCounts = z.strictObject({ hull_plating: count, electronics: count, drive_parts: count })

const spawnShipSchema = z.strictObject({
  def: z.string().min(1),
  profile: z.enum(['rookie', 'veteran', 'ace']),
  count: z.number().int().positive(),
})

const waveSchema = z.strictObject({ atS: nonNeg, ships: z.array(spawnShipSchema).min(1) })

export const relinkConfigSchema = z.strictObject({
  comment: z.string().optional(),
  /** An offline relay within this distance of the player is scanned (its repair list becomes known). */
  autoScanM: positive,
  /** Deliveries and the reboot start within this distance of the repair port... */
  portRangeM: positive,
  /** ...and below this speed. */
  portMaxSpeed: positive,
  /** A reboot aborts when the player is farther than this from the relay. */
  leaveAbortM: positive,
  /** The repair list per region (the region of the system the relay is repaired from). */
  parts: perRegion(partCounts),
  /** A long relay's repair list is the region's list times this, rounded up. */
  longRelayMult: z.number().min(1),
  reboot: z.strictObject({
    /** Defence time per region. */
    seconds: perRegion(positive),
    /** The objective's hull points (no shield). */
    hull: positive,
    /** `SiegeObjective.weight`: raiders weigh the relay at distance / weight against the nearest enemy ship. */
    siegeWeight: positive,
    /** Waves and guardians spawn on a ring this far from the relay. */
    spawnRadiusM: range,
    /** The objective sits this far inside the rim from the repair port (the ring's aperture is open). */
    objectiveInsetM: nonNeg,
    /** Reboot spawns (raiders, guardians, allies) despawn beyond this distance from the player. */
    despawnM: positive,
    waves: perRegion(z.array(waveSchema)),
    guardians: z.strictObject({
      /** Guardians join when a tetherstone-vein system is within this many relay hops (any relay state). */
      hops: count,
      /** ...at this fraction of the defence time. */
      atFraction: fraction,
      ships: z.array(spawnShipSchema),
    }),
    allies: z.strictObject({
      /** The chance allied patrols join at the start, when the system owner's faction patrols. */
      chance: fraction,
      /** Allies spawn on a ring this far from the relay. */
      radiusM: range,
      ships: z.array(spawnShipSchema),
    }),
  }),
  laneRepair: z.strictObject({
    /** A down lane ring within this distance can be repaired... */
    rangeM: positive,
    /** ...with these parts from the hold. */
    parts: z.strictObject({ hull_plating: count.optional(), electronics: count.optional(), drive_parts: count.optional() }),
  }),
})

export type RelinkConfig = z.infer<typeof relinkConfigSchema>
export type RelinkWave = RelinkConfig['reboot']['waves'][RegionId][number]

/** Parses and validates reach-relink.json (throws a ZodError naming the bad field). A fresh, mutable object. */
export function loadRelinkConfig(raw: unknown = relinkJson): RelinkConfig {
  return relinkConfigSchema.parse(raw)
}
